import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Form, Button, Alert, Spinner } from "react-bootstrap";
import { fetchWithAuth, extractErrorMessage } from "../../Functions";
import "../../assets/scss/components/custom-form.scss";

const AddReviews = () => {
    const API = process.env.REACT_APP_API_BASE_URL;
    const [review, setReview] = useState(null);
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });
    const initialFormState = {
        name: "",
        comment: "",
        // variable names from here are same as API's (snakecase) to avoid conflicts
        reviewer_type: "",
    }
    const [formState, setFormState] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false); // For form submission
    const navigate = useNavigate();
    const [formFeedback, setFormFeedback] = useState({ success: null, message: "" });
    const location = useLocation();
    const reviewID = location?.state?.reviewID;

    const fetchReview = useCallback(async (memberID) => {
        try {
            const response = await fetchWithAuth(`${API}/reviews/${memberID}`);
            const data = await response.json();

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFormFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to admin login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            if (!response.ok) {
                setFetchFeedback({ success: false, message: "Unable to load review, please refresh page." });
                console.error(data);
            }

            setReview(data);
            setFetchFeedback({ success: true, message: ""});
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error occured loading review."});
            console.error(error);
        }
    }, [API, navigate]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        // If UPDATE: Check that changes were detected before calling API
        if (review) {
            if (JSON.stringify(formState) === JSON.stringify(review)) {
                setFormFeedback({ success: false, message: "No changes detected." });
                return;
            }
        }

        setIsLoading(true);

        // Build form data
        const formData = new FormData();

        // Append form fields to form data
        Object.keys(formState).forEach((key) => {
            formData.append(key, formState[key]);
        })

        const isEdit = Boolean(review);
        const url = isEdit ? `${API}/reviews/update/${reviewID}/` : `${API}/reviews/create/`;
        const method = isEdit ? 'PUT' : 'POST';

        try {
            const response = await fetchWithAuth(url, {
                method: method,
                body: formData,
            });

            const data = await response.json();

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFormFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // This is the line that performs the navigation
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle other unsuccessful API responses
            if (!response.ok) {
                const defaultErrorMessage = `Unable to ${isEdit ? "update" : "add"} review, please try again`
                setFormFeedback({
                    success: false,
                    message: extractErrorMessage(data) || defaultErrorMessage,
                });

                // Log error to console
                console.error(data);
                return;
            }

            // Successful API response
            setFormFeedback({
                success: true,
                message: `Review successfully ${isEdit ? "updated" : "added"}!`,
            })
            // Clear form on successful ADDITION of review only
            if (!isEdit) {
                setFormState(initialFormState);
            }
        } catch (error) {
            setFormFeedback({
                success: false,
                message: extractErrorMessage(error) || `An unexpected error occured ${isEdit ? "updating" : "adding"} review.`,
            })
            console.error(error);
        } finally {
            setIsLoading(false);
        };
    }

    // Fetch review data if updating
    useEffect(() => {
        if (reviewID) {
            fetchReview(reviewID);
        }
    }, [reviewID, fetchReview])

    // Load review data into form
    useEffect(() => {
        if (review) {
            setFormState(review);
        }
    }, [review]);

    return (
        <>
            <h1 className="my-4 px-4 fw-semibold mb-4">{review ? "Edit" : "Add"} Review</h1>
            <p className="px-4 fs-6">Fill the form below to {review ? "edit" : "add a"} client's review.<small className="required text-danger fst-italic ms-1">&#40;All fields are required&#41;</small></p>

            {fetchFeedback.success === false && // Display this alert if fetchReview fails
                <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
            }

            <Form onSubmit={ handleSubmit} className="dashboard-form bg-white px-4 pt-3 pb-3">
                <Row xs={1} md={2}>
                    {/* Name */}
                    <Form.Group controlId="name" className="mb-4">
                        <Form.Label>Reviewer name</Form.Label> {/* Replace * with (required) */}
                        <Form.Control
                            type="text"
                            name="name"
                            value={ formState.name }
                            onChange={ handleChange }
                            required
                        />
                    </Form.Group>

                    {/* Reviewer Type */}
                    <Form.Group controlId="reviewer_type" className="mb-4">
                        <Form.Label>Reviewer type</Form.Label> {/* Replace * with (required) */}
                        <Form.Select
                            name="reviewer_type"
                            value={ formState.reviewer_type}
                            onChange={ handleChange }
                        >
                            <option value="">Select</option>
                            <option value="CLIENT">Client</option>
                            <option value="DEVELOPER">Developer</option>
                        </Form.Select>
                    </Form.Group>
                </Row>

                {/* Comment */}
                <Form.Group controlId="comment" className="mb-4">
                    <Form.Label>Comment</Form.Label> {/* Replace * with (required) */}
                    <Form.Control
                        as="textarea"
                        rows={10}
                        name="comment"
                        value={ formState.comment }
                        onChange={ handleChange }
                        required
                    />
                </Form.Group>

                <Button type="submit" className="px-3 px-md-5 mb-4">{review ? 'Update' : 'Add review'}</Button>
                <br></br>
                {isLoading === true &&
                    <div className="d-inline-block px-3 my-3">
                        <Spinner animation="border" className="d-flex align-self-center mx-auto" variant="primary" />
                        <p className="mt-2 text-center">{ review ? "Updating review" : "Adding review"}...</p>
                    </div>
                }

                { formFeedback.success &&
                    <Alert variant={formFeedback.success ? "primary" : "danger"} className="text-center">{ formFeedback.message }</Alert>
                }
            </Form>
        </>
    )
}

export default AddReviews;
